import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import {
  TimeEntry,
  Project,
  Client,
  timeEntriesAPI,
  projectsAPI,
  clientsAPI,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { useTranslation } from 'react-i18next';
import { FilterIcon, CheckIcon, XIcon, EditIcon, TrashIcon } from './icons';

interface TimeEntryListProps {
  onEditEntry: (entry: TimeEntry) => void;
  refreshTrigger?: number;
}

// Icons
const Icons = {
  filter: <FilterIcon className="w-4 h-4" strokeWidth={1.5} />,
  check: <CheckIcon className="w-4 h-4" strokeWidth={2} />,
  x: <XIcon className="w-4 h-4" strokeWidth={2} />,
  edit: <EditIcon className="w-4 h-4" strokeWidth={1.5} />,
  trash: <TrashIcon className="w-4 h-4" strokeWidth={1.5} />,
};

const TimeEntryList: React.FC<TimeEntryListProps> = ({
  onEditEntry,
  refreshTrigger,
}) => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEntries, setSelectedEntries] = useState<number[]>([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    status: '',
    sred_eligible: '',
    client_id: '',
    project_id: '',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const canApprove = user?.role === 'manager' || user?.role === 'admin';
  const canEditBilling = user?.role === 'manager' || user?.role === 'admin';

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    setError('');

    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== ''),
      );

      const response = await timeEntriesAPI.getAll(cleanFilters);
      const timeEntries = response.data.time_entries || response.data || [];
      setEntries(Array.isArray(timeEntries) ? timeEntries : []);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch time entries';
      setError(errorMessage);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectsAndClients = async () => {
    try {
      const [projectsResponse, clientsResponse] = await Promise.all([
        projectsAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setProjects(projectsResponse.data.projects || []);
      setClients(clientsResponse.data.clients || []);
    } catch (error) {
      console.error('Failed to load projects/clients:', error);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchProjectsAndClients();
  }, [refreshTrigger]);

  useEffect(() => {
    fetchEntries();
  }, [filters]);

  const handleApprove = async (entryId: number) => {
    try {
      await timeEntriesAPI.approve(entryId);
      fetchEntries();
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToApprove'),
        'error',
      );
    }
  };

  const handleReject = async (entryId: number) => {
    const reason = prompt(t('approval.rejectReason'));
    if (!reason) return;

    try {
      await timeEntriesAPI.reject(entryId, reason);
      fetchEntries();
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToReject'),
        'error',
      );
    }
  };

  const handleDelete = async (entryId: number) => {
    const confirmed = await confirm({
      title: t('timeEntry.deleteTitle'),
      message: t('timeEntry.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await timeEntriesAPI.delete(entryId);
      fetchEntries();
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToDelete'),
        'error',
      );
    }
  };

  const handleToggleBillable = async (
    entryId: number,
    currentValue: boolean,
  ) => {
    try {
      await timeEntriesAPI.update(entryId, { is_billable: !currentValue });
      fetchEntries();
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToBillable'),
        'error',
      );
    }
  };

  const handleSelectEntry = (entryId: number, checked: boolean) => {
    if (checked) {
      setSelectedEntries([...selectedEntries, entryId]);
    } else {
      setSelectedEntries(selectedEntries.filter((id) => id !== entryId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEntries(entries.map((entry) => entry.id!));
    } else {
      setSelectedEntries([]);
    }
  };

  const handleBulkUpdate = async (updates: Record<string, any>) => {
    if (selectedEntries.length === 0) {
      showToast(t('errors.selectEntries'), 'warning');
      return;
    }

    setBulkUpdating(true);
    try {
      await timeEntriesAPI.bulkUpdate(selectedEntries, updates);
      setSelectedEntries([]);
      setShowBulkEdit(false);
      fetchEntries();
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToUpdate'),
        'error',
      );
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = await confirm({
      title: t('timeEntry.deleteTitle'),
      message: `Delete ${selectedEntries.length} time ${selectedEntries.length === 1 ? 'entry' : 'entries'}?`,
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await timeEntriesAPI.bulkDelete(selectedEntries);
      setSelectedEntries([]);
      fetchEntries();
      showToast(`${selectedEntries.length} entries deleted`, 'success');
    } catch (error: any) {
      showToast(
        error.response?.data?.error || t('errors.failedToDelete'),
        'error',
      );
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      approved: isDark
        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
        : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      rejected: isDark
        ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
        : 'bg-rose-50 text-rose-700 border border-rose-200',
      needs_revision: isDark
        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
        : 'bg-amber-50 text-amber-700 border border-amber-200',
      pending: isDark
        ? 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
        : 'bg-slate-100 text-slate-600 border border-slate-200',
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  // Styling classes
  const inputClass = isDark
    ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-violet-500/20'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-violet-500/20';

  const cardClass = isDark
    ? 'bg-slate-900/30 border-slate-700/50'
    : 'bg-white border-slate-200';

  const formatEntryTime = (value?: string) => {
    if (!value) return '';

    const hhmmMatch = value.match(/^(\d{2}:\d{2})/);
    if (hhmmMatch) return hhmmMatch[1];

    try {
      return format(parseISO(value), 'HH:mm');
    } catch {
      return value;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div
        className={`p-5 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
      >
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center justify-between w-full sm:cursor-default"
        >
          <div className="flex items-center gap-2">
            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              {Icons.filter}
            </span>
            <h3
              className={`text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('common.filters')}
            </h3>
          </div>
          <svg
            className={`w-5 h-5 sm:hidden transition-transform ${filtersOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        <div
          className={`grid grid-cols-1 md:grid-cols-6 gap-4 mt-4 ${filtersOpen ? 'block' : 'hidden sm:grid'}`}
        >
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('timeEntry.fromDate')}
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) =>
                setFilters({ ...filters, date_from: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            />
          </div>
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('timeEntry.toDate')}
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) =>
                setFilters({ ...filters, date_to: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            />
          </div>
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('common.status')}
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('timeEntry.allStatus')}</option>
              <option value="pending">{t('timeEntry.pending')}</option>
              <option value="approved">{t('timeEntry.approved')}</option>
              <option value="rejected">{t('timeEntry.rejected')}</option>
              <option value="needs_revision">
                {t('timeEntry.needsRevision')}
              </option>
            </select>
          </div>
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('compliance.eligible')}
            </label>
            <select
              value={filters.sred_eligible}
              onChange={(e) =>
                setFilters({ ...filters, sred_eligible: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('common.all')}</option>
              <option value="true">{t('compliance.eligible')}</option>
              <option value="false">{t('compliance.notEligible')}</option>
            </select>
          </div>
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('timeEntry.client')}
            </label>
            <select
              value={filters.client_id}
              onChange={(e) =>
                setFilters({ ...filters, client_id: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('common.all')}</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {t('timeEntry.project')}
            </label>
            <select
              value={filters.project_id}
              onChange={(e) =>
                setFilters({ ...filters, project_id: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('common.all')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className={`mx-5 p-4 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400' : 'bg-rose-50 border border-rose-200 text-rose-600'}`}
        >
          {error}
        </div>
      )}

      {/* Bulk Actions */}
      {selectedEntries.length > 0 && (
        <div
          className={`mx-5 rounded-xl p-4 ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'}`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}
            >
              {selectedEntries.length} {t('common.entries')}{' '}
              {t('common.selected')}
            </span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowBulkEdit(!showBulkEdit)}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-medium hover:from-violet-500 hover:to-purple-500 transition-all"
              >
                {t('common.bulkEdit')}
              </button>
              <button
                onClick={async () => {
                  const confirmed = await confirm({
                    title: t('timeEntry.deleteTitle'),
                    message: `Delete ${selectedEntries.length} time ${selectedEntries.length === 1 ? 'entry' : 'entries'}?`,
                    confirmLabel: t('common.delete'),
                    cancelLabel: t('common.cancel'),
                    variant: 'danger',
                  });
                  if (!confirmed) return;
                  try {
                    await timeEntriesAPI.bulkDelete(selectedEntries);
                    setSelectedEntries([]);
                    fetchEntries();
                    showToast(
                      `${selectedEntries.length} entries deleted`,
                      'success',
                    );
                  } catch (error: any) {
                    showToast(
                      error.response?.data?.error || t('errors.failedToDelete'),
                      'error',
                    );
                  }
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium transition-all"
              >
                {t('common.delete')}
              </button>
              <button
                onClick={() => setSelectedEntries([])}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  isDark
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
              >
                {t('common.clearSelection')}
              </button>
            </div>
          </div>

          {showBulkEdit && (
            <div className="mt-4 space-y-4">
              {canEditBilling && (
                <div>
                  <h4
                    className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {t('manager.statusActions')}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <button
                      onClick={() => handleBulkUpdate({ is_billable: true })}
                      disabled={bulkUpdating}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-500 disabled:opacity-50 transition-all"
                    >
                      {t('billing.markBillable')}
                    </button>
                    <button
                      onClick={() => handleBulkUpdate({ is_billable: false })}
                      disabled={bulkUpdating}
                      className={`px-3 py-2 rounded-lg text-sm disabled:opacity-50 transition-all ${
                        isDark
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-slate-300 text-slate-700 hover:bg-slate-400'
                      }`}
                    >
                      {t('billing.markNonBillable')}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <h4
                  className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('manager.changeProject')}
                </h4>
                <div className="flex gap-2">
                  <select
                    id="bulk-project-select"
                    className={`flex-1 px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${inputClass}`}
                  >
                    <option value="">
                      {t('manager.selectProjectPlaceholder')}
                    </option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                        {project.is_internal
                          ? ` (${t('common.internal')})`
                          : project.client
                            ? ` - ${project.client.name}`
                            : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      const select = document.getElementById(
                        'bulk-project-select',
                      ) as HTMLSelectElement;
                      const projectId = select.value
                        ? Number(select.value)
                        : null;
                      if (projectId) {
                        handleBulkUpdate({ project_id: projectId });
                        select.value = '';
                      }
                    }}
                    disabled={bulkUpdating}
                    className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-medium hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 transition-all whitespace-nowrap"
                  >
                    {t('manager.updateProject')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Time Entries Header */}
      <div
        className={`px-5 py-3 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
      >
        <h3
          className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {t('manager.timeEntries')}
          <span
            className={`ml-2 px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}
          >
            {entries.length}
          </span>
        </h3>
      </div>

      {entries.length === 0 ? (
        <div
          className={`p-12 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-slate-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          {t('timeEntry.noEntries')}
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="md:hidden px-4 pb-4 space-y-3">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className={`rounded-xl p-4 ${
                  isDark
                    ? 'bg-slate-800/50 border border-slate-700/50'
                    : 'bg-white border border-slate-200 shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Date & Time */}
                    <div className="flex flex-col  gap-2 mb-1">
                      <span
                        className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {format(parseISO(entry.entry_date), 'dd MMM yyyy')}
                      </span>
                      {entry.start_time && entry.end_time && (
                        <span
                          className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          {formatEntryTime(entry.start_time)} -{' '}
                          {formatEntryTime(entry.end_time)}
                        </span>
                      )}
                    </div>
                    {/* Project & Client */}
                    <div
                      className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                    >
                      {entry.project?.name}
                    </div>
                    {entry.project?.client && (
                      <div
                        className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                      >
                        {entry.project.client.name}
                      </div>
                    )}
                    {/* Description */}
                    {entry.description && (
                      <p
                        className={`text-sm mt-2 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {entry.description}
                      </p>
                    )}
                  </div>
                  {/* Hours */}
                  <div className={`text-right flex-shrink-0`}>
                    <div
                      className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      {(
                        parseFloat(entry.hours_worked?.toString() || '0') || 0
                      ).toFixed(1)}
                      h
                    </div>
                  </div>
                </div>

                {/* Badges Row */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {/* Status Badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusBadge(entry.approval_status || 'pending')}`}
                  >
                    {entry.approval_status || 'pending'}
                  </span>
                  {/* SR&ED/CDAE Badges */}
                  {entry.is_sred_eligible && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                        isDark
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}
                    >
                      SR&ED
                    </span>
                  )}
                  {entry.is_cdae_eligible && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                        isDark
                          ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                          : 'bg-purple-50 text-purple-700 border border-purple-200'
                      }`}
                    >
                      CDAE
                    </span>
                  )}
                  {canEditBilling && entry.is_billable && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                        isDark
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {t('billing.billable')}
                    </span>
                  )}
                </div>

                {/* Actions Row */}
                <div
                  className={`flex items-center justify-end gap-1 mt-3 pt-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                >
                  <button
                    onClick={() => onEditEntry(entry)}
                    className={`p-2 rounded-lg transition-all ${
                      isDark
                        ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                    title={t('common.edit')}
                  >
                    {Icons.edit}
                  </button>
                  {canApprove && entry.approval_status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(entry.id!)}
                        className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                        title={t('approval.approve')}
                      >
                        {Icons.check}
                      </button>
                      <button
                        onClick={() => handleReject(entry.id!)}
                        className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                        title={t('approval.reject')}
                      >
                        {Icons.x}
                      </button>
                    </>
                  )}
                  {entry.approval_status !== 'approved' && (
                    <button
                      onClick={() => handleDelete(entry.id!)}
                      className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      title={t('common.delete')}
                    >
                      {Icons.trash}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div
            ref={tableContainerRef}
            className={`hidden md:block overflow-auto max-h-[calc(100vh-320px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <table className="min-w-full relative">
              <thead
                className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
              >
                <tr
                  className={
                    isDark
                      ? 'border-b border-slate-700/50'
                      : 'border-b border-slate-200'
                  }
                >
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedEntries.length === entries.length &&
                        entries.length > 0
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    />
                  </th>
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('timeEntry.date')}
                  </th>
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('timeEntry.project')}
                  </th>
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('timeEntry.hours')}
                  </th>
                  {canEditBilling && (
                    <>
                      <th
                        className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('billing.billable')}
                      </th>
                    </>
                  )}
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('timeEntry.description')}
                  </th>
                  <th
                    className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('common.status')}
                  </th>
                  <th
                    className={`px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  isDark
                    ? 'divide-y divide-slate-700/50'
                    : 'divide-y divide-slate-100'
                }
              >
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={`transition-colors ${isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-5 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedEntries.includes(entry.id!)}
                        onChange={(e) =>
                          handleSelectEntry(entry.id!, e.target.checked)
                        }
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      />
                    </td>
                    <td
                      className={`px-5 py-4 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      <div className="font-medium">
                        {format(parseISO(entry.entry_date), 'MMM dd, yyyy')}
                      </div>
                      {entry.start_time && entry.end_time && (
                        <div
                          className={`text-sm mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          {formatEntryTime(entry.start_time)} - <br />
                          {formatEntryTime(entry.end_time)}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm">
                      <div
                        className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {entry.project?.name}
                      </div>
                      {entry.project?.client && (
                        <div
                          className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          {entry.project.client.name}
                        </div>
                      )}
                      <div className="flex gap-1 mt-1.5">
                        {entry.is_sred_eligible && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                              isDark
                                ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}
                          >
                            SR&ED
                          </span>
                        )}
                        {entry.is_cdae_eligible && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                              isDark
                                ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                : 'bg-purple-50 text-purple-700 border border-purple-200'
                            }`}
                          >
                            CDAE
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className={`px-5 py-4 whitespace-nowrap text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      {(
                        parseFloat(entry.hours_worked?.toString() || '0') || 0
                      ).toFixed(1)}
                      h
                    </td>
                    {canEditBilling && (
                      <>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              handleToggleBillable(
                                entry.id!,
                                !!entry.is_billable,
                              )
                            }
                            disabled={
                              entry.approval_status === 'approved' &&
                              !canApprove
                            }
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all disabled:opacity-50 ${
                              entry.is_billable
                                ? isDark
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : isDark
                                  ? 'bg-slate-700 text-slate-400'
                                  : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {entry.is_billable && Icons.check}
                            {entry.is_billable
                              ? t('common.yes')
                              : t('common.no')}
                          </button>
                        </td>
                      </>
                    )}
                    <td
                      className={`px-5 py-4 text-sm max-w-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                    >
                      <div className="truncate" title={entry.description}>
                        {entry.description}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusBadge(entry.approval_status || 'pending')}`}
                      >
                        {entry.approval_status || 'pending'}
                      </span>
                      {entry.approved_by && (
                        <div
                          className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          {t('timeEntry.by')} {entry.approved_by.full_name}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onEditEntry(entry)}
                          className={`p-2 rounded-lg transition-all ${
                            isDark
                              ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                          }`}
                          title={t('common.edit')}
                        >
                          {Icons.edit}
                        </button>

                        {canApprove && entry.approval_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(entry.id!)}
                              className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                              title={t('approval.approve')}
                            >
                              {Icons.check}
                            </button>
                            <button
                              onClick={() => handleReject(entry.id!)}
                              className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                              title={t('approval.reject')}
                            >
                              {Icons.x}
                            </button>
                          </>
                        )}

                        {entry.approval_status !== 'approved' && (
                          <button
                            onClick={() => handleDelete(entry.id!)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TimeEntryList;
